import { io } from 'socket.io-client'
import Config from 'aldoo-ra/Config'
import Value from 'aldoo-ra/Value'
import { useEffect } from 'react'

// Store active socket connections
const socketConnections = new Map()

// This is a reference to the Value('user-credentials') object
// It changes based on the user login state (same pattern as API module)
let USER_CREDENTIALS = null

/**
 * This is added to the application start up and listens for
 * available user credentials. Those are then used to make
 * realtime connections to the backend (same pattern as AldooAPI)
 */
export const AldooRealtime = () => {
  const [auth] = Value("user-credentials")

  useEffect(() => {
    // Here we get the value from the auth object
    // If it's not available, we try to get the value immediately
    USER_CREDENTIALS = auth
  }, [auth])
}

/**
 * Create or get existing socket connection for a namespace
 * @param {string} namespace - The namespace to connect to (e.g., "deployment")
 * @param {Object} options - Socket connection options
 * @returns {Socket} Socket.io client instance
 */
const createSocket = (namespace, options = {}) => {
  // Get auth token from stored credentials (same pattern as API module)
  let authToken = null
  if (USER_CREDENTIALS?.auth_token) {
    authToken = USER_CREDENTIALS.auth_token
  }
  
  if (!authToken) {
    throw new Error("Authentication required for realtime connections")
  }

  const socketKey = namespace
  
  // Return existing connection if available and connected
  if (socketConnections.has(socketKey)) {
    const existingSocket = socketConnections.get(socketKey)
    if (existingSocket.connected) {
      return existingSocket
    } else {
      // Clean up disconnected socket
      existingSocket.disconnect()
      socketConnections.delete(socketKey)
    }
  }

  // Create new socket connection
  const socketUrl = `${Config.PLATFORM_API_URL}/rt/${namespace}`
  
  const defaultOptions = {
    auth: { token: authToken },
    timeout: 10000,
    reconnection: true,
    reconnectionAttempts: Infinity, // Keep trying indefinitely
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    transports: ['websocket', 'polling'], // Prefer WebSockets, fallback to polling
    upgrade: true, // Allow transport upgrades
    rememberUpgrade: true, // Remember successful upgrades
    pingTimeout: 60000, // Increase ping timeout to 60 seconds
    pingInterval: 25000, // Send ping every 25 seconds
    ...options
  }

  const socket = io(socketUrl, defaultOptions)

  // Store connection
  socketConnections.set(socketKey, socket)

  // Handle connection events
  socket.on('connect', () => {
    // Log transport upgrades
    socket.io.engine.on('upgrade', () => {
      console.log(`Realtime: Transport upgraded to: ${socket.io.engine.transport.name}`)
    })
  })

  socket.on('disconnect', (reason) => {
    // Silent disconnect - only log critical issues
    if (reason === 'io server disconnect' || reason === 'ping timeout') {
      console.log(`Realtime: Connection lost (${reason})`)
    }
  })

  socket.on('connect_error', (error) => {
    // Only log persistent connection errors, not temporary websocket failures
    if (error.message !== 'websocket error') {
      console.error(`Realtime: Connection error for ${namespace}:`, error.message)
    }
  })

  return socket
}

/**
 * Disconnect and cleanup socket connection
 * @param {string} namespace - The namespace to disconnect from
 */
const disconnectSocket = (namespace) => {
  const socketKey = namespace
  if (socketConnections.has(socketKey)) {
    const socket = socketConnections.get(socketKey)
    socket.disconnect()
    socketConnections.delete(socketKey)
    console.log(`Realtime: Disconnected from ${namespace} namespace`)
  }
}

/**
 * Disconnect all socket connections
 */
const disconnectAll = () => {
  socketConnections.forEach((socket, namespace) => {
    socket.disconnect()
    console.log(`Realtime: Disconnected from ${namespace} namespace`)
  })
  socketConnections.clear()
}

/**
 * Get existing socket connection without creating new one
 * @param {string} namespace - The namespace to get
 * @returns {Socket|null} Socket instance or null if not connected
 */
const getSocket = (namespace) => {
  const socketKey = namespace
  const socket = socketConnections.get(socketKey)
  return socket && socket.connected ? socket : null
}

/**
 * Main realtime function - similar to api() function
 * Creates or returns socket connection for the specified namespace
 * @param {string} namespace - The namespace to connect to
 * @param {Object} options - Socket connection options
 * @returns {Socket} Socket.io client instance
 */
const rt = (namespace, options = {}) => {
  if (!namespace) {
    throw new Error("Namespace is required for realtime connections")
  }
  
  return createSocket(namespace, options)
}

// Export main function and utilities
export default rt
export { 
  rt,
  createSocket,
  disconnectSocket, 
  disconnectAll,
  getSocket
}